import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useSession } from "../../app/session.js";
import { trpc, trpcErrorCode } from "../../app/trpc.js";
import { BrandMark, Button, Field, focusRing, Input, Notice, Screen } from "../../ui/index.js";
import { LocaleSwitch } from "./LocaleSwitch.js";

/** Вход по коду на почту: шаг с почтой, затем шаг с шестизначным кодом. */
export function LoginPage({ next = "/" }: { next?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");

  const request = useMutation(trpc.auth.requestCode.mutationOptions({ onSuccess: () => setStep("code") }));
  const verify = useMutation(
    trpc.auth.verifyCode.mutationOptions({
      onSuccess: async () => {
        await session.refresh();
        navigate(next, { replace: true });
      },
    }),
  );

  const onEmail = (e: FormEvent) => {
    e.preventDefault();
    request.mutate({ email: email.trim() });
  };
  const onCode = (e: FormEvent) => {
    e.preventDefault();
    verify.mutate({ email: email.trim(), code: code.trim() });
  };

  const errorText = (err: unknown) => {
    const c = trpcErrorCode(err);
    if (c === "conflict") return t("login.errorRate");
    if (c === "unauthenticated") return t("login.errorCode");
    return t("common.error");
  };

  return (
    <Screen>
      <div className="mb-6 flex items-start justify-between gap-4">
        <Link to="/" className={`inline-flex rounded ${focusRing}`} aria-label="AI Administrator">
          <BrandMark className="h-10 w-10" />
        </Link>
        <LocaleSwitch />
      </div>
      <h1 className="mb-6 text-2xl font-semibold text-ink">{t("login.title")}</h1>
      {step === "email" ? (
        <form onSubmit={onEmail} className="space-y-4">
          <Field label={t("login.emailLabel")} htmlFor="email">
            <Input id="email" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {request.isError && <Notice kind="error">{errorText(request.error)}</Notice>}
          <Button type="submit" className="w-full" size="lg" loading={request.isPending}>
            {t("login.sendCode")}
          </Button>
        </form>
      ) : (
        <form onSubmit={onCode} className="space-y-4">
          <Notice kind="success">{t("login.codeSent", { email })}</Notice>
          <Field label={t("login.codeLabel")} htmlFor="code">
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              fieldSize="lg"
              className="text-center font-semibold tracking-[0.4em]"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </Field>
          {verify.isError && <Notice kind="error">{errorText(verify.error)}</Notice>}
          <Button type="submit" className="w-full" size="lg" loading={verify.isPending} disabled={code.length !== 6}>
            {t("login.verify")}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => setStep("email")}>
            {t("login.back")}
          </Button>
        </form>
      )}
    </Screen>
  );
}
